import type {
  ProductCreateInput,
  ProductUpdateInput,
  VariantUpdateInput,
} from "@/modules/products/domain/contracts";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { toast } from "@devhop/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { productsApi } from "./client";

export const productKeys = {
  all: ["products"] as const,
  list: (
    q: Partial<
      OffsetPageQueryDTO & {
        search?: string;
        status?: "draft" | "active" | "archived";
        kind?: "retail" | "production" | "hybrid" | "online-showcase";
        isSellable?: boolean;
        categoryIds?: string[];
        tagIds?: string[];
      }
    >,
  ) => ["products", "list", q] as const,
  detail: (id: string) => ["products", "detail", id] as const,
};

export function useProductsQuery(
  query: Partial<
    OffsetPageQueryDTO & {
      search?: string;
      status?: "draft" | "active" | "archived";
      kind?: "retail" | "production" | "hybrid" | "online-showcase";
      isSellable?: boolean;
      categoryIds?: string[];
      tagIds?: string[];
    }
  > = {},
) {
  const q: OffsetPageQueryDTO & {
    search?: string;
    status?: "draft" | "active" | "archived";
    kind?: "retail" | "production" | "hybrid" | "online-showcase";
    isSellable?: boolean;
    categoryIds?: string[];
    tagIds?: string[];
  } = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
    search: query.search,
    status: query.status,
    kind: query.kind,
    isSellable: query.isSellable,
    categoryIds: query.categoryIds,
    tagIds: query.tagIds,
  };
  return useQuery({
    queryKey: productKeys.list(q),
    queryFn: () => productsApi.list(q),
  });
}

export function useProductQuery(id: string) {
  return useQuery({
    queryKey: productKeys.detail(id),
    queryFn: () => productsApi.get(id),
    enabled: !!id,
  });
}

export function useCreateProduct() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductCreateInput) => productsApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productKeys.all });
      toast.success("ສ້າງສິນຄ້າສໍາເລັດ");
    },
    onError: (error: any) => {
      if (error.message?.includes("already exists")) {
        toast.error("ຊື່ Slug ນີ້ມີແລ້ວ", {
          description: "ກະລຸນາປ່ຽນຊື່ອັງກິດເພື່ອສ້າງ Slug ໃໝ່",
        });
      } else {
        toast.error("ສ້າງສິນຄ້າລົ້ມເຫຼວ", {
          description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
        });
      }
    },
  });
}

export function useUpdateProduct(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ProductUpdateInput) => productsApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productKeys.detail(id) });
      qc.invalidateQueries({ queryKey: productKeys.all });
      toast.success("ແກ້ໄຂສິນຄ້າສໍາເລັດ");
    },
    onError: (error: any) => {
      if (error.message?.includes("already exists")) {
        toast.error("ຊື່ Slug ນີ້ມີແລ້ວ", {
          description: "ກະລຸນາປ່ຽນຊື່ອັງກິດເພື່ອສ້າງ Slug ໃໝ່",
        });
      } else {
        toast.error("ແກ້ໄຂສິນຄ້າລົ້ມເຫຼວ", {
          description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
        });
      }
    },
  });
}

export function useDeleteProduct() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => productsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: productKeys.all });
    },
  });
  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });
  return { ...base, run };
}

// ==================== Variants ====================
export function useVariantsQuery(productId: string) {
  return useQuery({
    queryKey: ["products", productId, "variants"],
    queryFn: () => productsApi.listVariants(productId),
    enabled: !!productId,
  });
}

export function useGenerateVariants(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body?: {
      price?: number;
      compareAtPrice?: number;
      weightGrams?: number;
    }) => productsApi.generateVariants(productId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "variants"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success("ສ້າງ Variants ສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ສ້າງ Variants ລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useCreateVariant(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof productsApi.createVariant>[1]) =>
      productsApi.createVariant(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "variants"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success("ສ້າງ Variant ສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ສ້າງ Variant ລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useUpdateVariant(productId: string, variantId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof productsApi.updateVariant>[2]) =>
      productsApi.updateVariant(productId, variantId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "variants"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success("ແກ້ໄຂ Variant ສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ແກ້ໄຂ Variant ລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useBulkUpdateVariants(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { variantIds: string[]; payload: VariantUpdateInput }) =>
      productsApi.bulkUpdateVariants(productId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "variants"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success(`ແກ້ໄຂ ${data.updated} variants ສໍາເລັດ`);
    },
    onError: (error: any) => {
      toast.error("ແກ້ໄຂ Variants ລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useDeleteVariant(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variantId: string) =>
      productsApi.deleteVariant(productId, variantId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "variants"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success("ລຶບ Variant ສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ລຶບ Variant ລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useBulkDeleteVariants(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { variantIds: string[] }) =>
      productsApi.bulkDeleteVariants(productId, body),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "variants"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success(`ລຶບ ${data.deleted} variants ສໍາເລັດ`);
    },
    onError: (error: any) => {
      toast.error("ລຶບ Variants ລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

// ==================== Images ====================
export function useProductImagesQuery(productId: string) {
  return useQuery({
    queryKey: ["products", productId, "images"],
    queryFn: () => productsApi.listImages(productId),
    enabled: !!productId,
  });
}

export function useCreateProductImage(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof productsApi.createImage>[1]) =>
      productsApi.createImage(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "images"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success("ເພີ່ມຮູບສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ເພີ່ມຮູບລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useCreateProductImagesBulk(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      fileKeys: string[];
      blurhashes?: string[];
    }) =>
      productsApi.createImagesBulk(productId, {
        fileKeys: payload.fileKeys,
        blurhashes: payload.blurhashes,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "images"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success(
        data.length > 0
          ? `ເພີ່ມ ${data.length} ຮູບສໍາເລັດ`
          : "ເພີ່ມຮູບສໍາເລັດ",
      );
    },
    onError: (error: unknown) => {
      toast.error("ເພີ່ມຮູບລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useDeleteProductImage(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: string) =>
      productsApi.deleteImage(productId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "images"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success("ລຶບຮູບສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ລຶບຮູບລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useBulkDeleteProductImages(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageIds: string[]) =>
      productsApi.bulkDeleteImages(productId, { imageIds }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "images"],
      });
      queryClient.invalidateQueries({ queryKey: ["products", productId] });
      toast.success(
        data.deleted > 0 ? `ລຶບ ${data.deleted} ຮູບສໍາເລັດ` : "ລຶບຮູບສໍາເລັດ",
      );
    },
    onError: (error: unknown) => {
      toast.error("ລຶບຮູບລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}

export function useReorderProductImages(productId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof productsApi.reorderImages>[1]) =>
      productsApi.reorderImages(productId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["products", productId, "images"],
      });
      toast.success("ຈັດຮຽງຮູບສໍາເລັດ");
    },
    onError: (error: any) => {
      toast.error("ຈັດຮຽງຮູບລົ້ມເຫຼວ", {
        description: error.message || "ເກີດຂໍ້ຜິດພາດບາງຢ່າງ",
      });
    },
  });
}
